// FAQArticleView.jsx — страница одной статьи F.A.Q.
// backLink + шапка статьи (категория, мета) + заголовок + обложка + контент +
// автор + секция комментариев с формой добавления.

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AdminService from '../../../API/AdminService';
import UserService from '../../../API/UserService';
import { ADMIN_addres, FOLDER_PROFILE } from '../../../API/API';
import cl from './FAQArticleView.module.css';

import { renderMarkdown } from '../../../utils/markdown';
import logger from '../../../utils/logger';
import {
    IconArrowLeft, IconEdit, IconClock, IconEye, IconMessage,
    IconAlert, IconRefresh, IconBook, IconUser,
} from '../../../components/Icons';

// Подмена «битых» URL картинок на стабильный путь через api-admin.
// На бэке статьи лежат с устаревшими доменами:
//   https://adminpanelminecraftia.ru/assets/articles/file.jpg
//   https://minecraftia-rus.online/assets/articles/file.jpg
// Эти домены уже не отдают картинки. Реально файлы доступны по адресу
// https://minecraftia-school.ru/api-admin/assets/articles/file.jpg.
// Подменяем глобально — и в markdown ![alt](url), и в HTML <img src="…">,
// и в произвольном тексте. На бэке не правим — этими статьями могут
// пользоваться другие сервисы (тот же подход на сайте СтартКласс).
function rewriteUrl(url) {
    if (!url) return url;
    return url
        .replace(/https?:\/\/adminpanelminecraftia\.ru\/assets\//gi,
                 'https://minecraftia-school.ru/api-admin/assets/')
        .replace(/https?:\/\/minecraftia-rus\.online\/assets\//gi,
                 'https://minecraftia-school.ru/api-admin/assets/');
}

// Резолв URL картинки-обложки. Если URL абсолютный — пропускаем через rewriteUrl
// для подмены устаревших доменов. Если относительный — добавляем prefix
// admin-сервиса.
function resolveImageUrl(raw) {
    if (!raw) return null;
    if (raw.startsWith('http://') || raw.startsWith('https://') || raw.startsWith('//')) {
        return rewriteUrl(raw);
    }
    if (raw.startsWith('/')) {
        return `${ADMIN_addres}${raw}`;
    }
    return `${ADMIN_addres}/assets/articles/${raw}`;
}

// Полная подмена устаревших доменов в строке любого формата.
// Применяется ко всему тексту контента до парсинга markdown — ловит
// и ![alt](url), и <img src="…">, и плоский текст.
function rewriteAllUrls(text) {
    if (!text) return '';
    return rewriteUrl(text);
}

const FAQArticleView = () => {
    const navigate = useNavigate();
    const { articleId } = useParams();
    const [article, setArticle] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [currentUser, setCurrentUser] = useState(null);
    const [newComment, setNewComment] = useState('');
    const [isAddingComment, setIsAddingComment] = useState(false);
    const [coverFailed, setCoverFailed] = useState(false);

    const parseMarkdown = renderMarkdown;

    useEffect(() => {
        loadArticle();
        loadCurrentUser();
    }, [articleId]);

    const loadCurrentUser = async () => {
        try {
            // В UserService нет getProfile — правильное имя getMyProfile.
            const userInfo = await UserService.getMyProfile();
            setCurrentUser(userInfo);
        } catch (err) {
            logger.error('Ошибка получения профиля пользователя:', err);
        }
    };

    const loadArticle = async () => {
        try {
            setLoading(true);
            const response = await AdminService.getFAQArticle(articleId);
            setArticle(response.data);
            setError('');
            setCoverFailed(false);
        } catch (err) {
            setError('Статья не найдена');
            logger.error('Ошибка загрузки статьи FAQ:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleCommentSubmit = async (e) => {
        e.preventDefault();
        if (!newComment.trim()) return;

        const errors = AdminService.validateFAQCommentData({ content: newComment });
        if (errors.length > 0) {
            alert(errors.join('\n'));
            return;
        }

        try {
            setIsAddingComment(true);
            await AdminService.createFAQComment(articleId, { content: newComment.trim() });
            setNewComment('');
            await loadArticle();
        } catch (err) {
            logger.error('Ошибка добавления комментария:', err);
            alert('Ошибка добавления комментария');
        } finally {
            setIsAddingComment(false);
        }
    };

    const handleEditArticle = () => {
        navigate(`/main/faq/edit/${articleId}`);
    };

    const isOwner = currentUser && currentUser.id === 1;

    if (loading) {
        return (
            <div className={cl.page_wrapper}>
                <div className={cl.wrap_content_1}>
                    <div className={cl.loadingContainer}>
                        <div className={cl.spinner}/>
                        <p>Загрузка статьи…</p>
                    </div>
                </div>
            </div>
        );
    }

    if (error || !article) {
        return (
            <div className={cl.page_wrapper}>
                <div className={cl.wrap_content_1}>
                    <div className={cl.errorState}>
                        <span className={cl.errorIcon}>
                            <IconAlert width={20} height={20}/>
                        </span>
                        <h3>Не удалось загрузить статью</h3>
                        <p>{error || 'Статья не найдена'}</p>
                        <button onClick={() => navigate('/main/faq')} className={cl.retryButton}>
                            <IconArrowLeft width={14} height={14}/>
                            <span>Вернуться к F.A.Q.</span>
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // Обложка: пробуем `cover_image_url`, потом `featured_image`.
    const coverUrl = !coverFailed
        ? (resolveImageUrl(article.cover_image_url) || resolveImageUrl(article.featured_image))
        : null;

    return (
        <div className={cl.page_wrapper}>
            <div className={cl.wrap_content_1}>
                {/* Верхняя панель: назад + редактировать */}
                <div className={cl.topBar}>
                    <button
                        onClick={() => navigate(`/main/faq/category/${article.category.name}`)}
                        className={cl.backLink}
                    >
                        <IconArrowLeft width={14} height={14}/>
                        <span>К {article.category.title}</span>
                    </button>
                    {isOwner && (
                        <button onClick={handleEditArticle} className={cl.btnSecondary}>
                            <IconEdit width={14} height={14}/>
                            <span>Редактировать</span>
                        </button>
                    )}
                </div>

                {/* Карточка статьи */}
                <article className={cl.articleCard}>
                    {/* Метаинфо сверху */}
                    <div className={cl.articleMeta}>
                        <span className={cl.categoryBadge}>
                            <IconBook width={12} height={12}/>
                            {article.category.title}
                        </span>
                        <span className={cl.metaItem}>
                            <IconClock width={12} height={12}/>
                            {AdminService.formatReadTime(article.estimated_read_time)}
                        </span>
                        <span className={cl.metaItem}>
                            <IconEye width={12} height={12}/>
                            {AdminService.formatLargeNumber(article.views_count)}
                            <span className={cl.metaWord}>
                                {pluralRu(article.views_count, 'просмотр', 'просмотра', 'просмотров')}
                            </span>
                        </span>
                        <span className={cl.metaItem}>
                            <IconMessage width={12} height={12}/>
                            {article.comments_count}
                            <span className={cl.metaWord}>
                                {pluralRu(article.comments_count, 'комментарий', 'комментария', 'комментариев')}
                            </span>
                        </span>
                    </div>

                    {/* Заголовок */}
                    <h1 className={cl.title}>{article.title}</h1>

                    {/* Обложка — только если URL валидный и загрузилась */}
                    {coverUrl && (
                        <div className={cl.coverImage}>
                            <img
                                src={coverUrl}
                                alt={article.title}
                                onError={() => setCoverFailed(true)}
                            />
                        </div>
                    )}

                    {/* Контент Markdown.
                        Картинки в content приходят с устаревшими доменами
                        (adminpanelminecraftia.ru / minecraftia-rus.online),
                        которые сейчас отдают 404. Подменяем глобально на
                        actual-домен api-admin перед парсингом markdown. */}
                    <div
                        className={cl.content}
                        dangerouslySetInnerHTML={{
                            __html: parseMarkdown(rewriteAllUrls(article.content)),
                        }}
                    />

                    {/* Автор */}
                    {article.author && (
                        <div className={cl.author}>
                            <ArticleAuthorAvatar author={article.author}/>
                            <div>
                                <div className={cl.authorLabel}>Автор статьи</div>
                                <div className={cl.authorName}>{article.author.name}</div>
                            </div>
                        </div>
                    )}
                </article>

                {/* Комментарии */}
                <section className={cl.commentsSection}>
                    <h2 className={cl.commentsTitle}>
                        Комментарии ({article.comments_count})
                    </h2>

                    {/* Форма добавления */}
                    <div className={cl.addCommentForm}>
                        <form onSubmit={handleCommentSubmit}>
                            <textarea
                                value={newComment}
                                onChange={(e) => setNewComment(e.target.value)}
                                placeholder="Напишите ваш комментарий…"
                                className={cl.commentTextarea}
                                rows={4}
                                maxLength={2000}
                            />
                            <div className={cl.commentActions}>
                                <span className={cl.charCounter}>
                                    {newComment.length}/2000
                                </span>
                                <button
                                    type="submit"
                                    className={cl.addCommentButton}
                                    disabled={isAddingComment || !newComment.trim()}
                                >
                                    {isAddingComment ? 'Добавление…' : 'Добавить комментарий'}
                                </button>
                            </div>
                        </form>
                    </div>

                    {/* Список комментариев */}
                    {article.comments && article.comments.length > 0 ? (
                        <div className={cl.commentsList}>
                            {article.comments.map(comment => (
                                <CommentItem key={comment.id} comment={comment} />
                            ))}
                        </div>
                    ) : (
                        <div className={cl.noComments}>
                            Пока нет комментариев. Будьте первым!
                        </div>
                    )}
                </section>
            </div>
        </div>
    );
};


// ----- Аватар автора статьи (отдельный компонент с fallback) -----
const ArticleAuthorAvatar = ({ author }) => {
    const [failed, setFailed] = useState(false);
    if (!author.avatar || failed) {
        return (
            <div className={cl.authorAvatarFallback}>
                {(author.name?.[0] || '?').toUpperCase()}
            </div>
        );
    }
    return (
        <img
            src={`${FOLDER_PROFILE}${author.avatar}`}
            alt={author.name}
            className={cl.authorAvatar}
            onError={() => setFailed(true)}
        />
    );
};


// ----- Комментарий -----
const CommentItem = ({ comment }) => {
    const [authorAvatar, setAuthorAvatar] = useState(null);
    const [avatarFailed, setAvatarFailed] = useState(false);

    useEffect(() => {
        const loadAuthorAvatar = async () => {
            try {
                const authorInfo = await UserService.getUserById(comment.author_id);
                if (authorInfo && authorInfo.data && authorInfo.data.photoLink) {
                    setAuthorAvatar(`${FOLDER_PROFILE}${authorInfo.data.photoLink}`);
                }
            } catch (err) {
                logger.error('Ошибка загрузки аватарки автора:', err);
            }
        };
        if (comment.author_id) loadAuthorAvatar();
    }, [comment.author_id]);

    const showImg = authorAvatar && !avatarFailed;
    const initial = (comment.author_name?.[0] || '?').toUpperCase();

    return (
        <div className={cl.commentItem}>
            <div className={cl.commentAvatarWrap}>
                {showImg ? (
                    <img
                        src={authorAvatar}
                        alt={comment.author_name}
                        className={cl.commentAvatar}
                        onError={() => setAvatarFailed(true)}
                    />
                ) : (
                    <div className={cl.commentAvatarFallback}>{initial}</div>
                )}
            </div>
            <div className={cl.commentBody}>
                <div className={cl.commentHead}>
                    <span className={cl.commentAuthor}>{comment.author_name}</span>
                    <span className={cl.commentDate}>
                        {AdminService.formatDate(comment.created_at)}
                    </span>
                </div>
                <div className={cl.commentContent}>
                    {comment.content.split('\n').map((p, i) => (
                        <p key={i}>{p}</p>
                    ))}
                </div>
            </div>
        </div>
    );
};

function pluralRu(n, one, few, many) {
    const mod10 = n % 10;
    const mod100 = n % 100;
    if (mod10 === 1 && mod100 !== 11) return one;
    if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few;
    return many;
}

export default FAQArticleView;
